import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { LoadingScreenService } from "./loading-screen.service";
import { SharedService } from "./shared.service";
import { SoApiService } from "./soapi.service";
import { LoadedContextService } from "./loadedContext.service";
import { BladeService } from "./blade.service";

@Injectable({
  providedIn: "root"
})
export class LandingScreenResolver {
  constructor(
    private readonly so: SoApiService,
    private readonly sharedService: SharedService,
    private readonly loadingScreen: LoadingScreenService,
    private readonly loadedContextService: LoadedContextService,
    private bladeService: BladeService
  ) { }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<any> {
    // Blade info is set in the appointment components
    if (this.loadedContextService.isComposeAppointment() ||
      this.loadedContextService.isReadAppointment()) {
      return Promise.resolve();
    }

    // Compose-mode components manage their own loading state via ngOnInit.
    // Do NOT call startLoading() here — the resolver returns synchronously and the
    // component is reused on every tab-switch (ngOnInit won't re-run to call stopLoading()).
    if (this.loadedContextService.isComposeMode()) {
      return Promise.resolve();
    }

    this.loadingScreen.startLoading();
    return this.configure();
  }

  // To improve performance and load data only once.
  // On first load save email ID in State and when navigation occurs check does
  // the selected email changed. If not, do not repeatedly load data.
  private async configure() {
    await this.waitForItemId();

    let emailId: string;
    try {
      emailId = this.sharedService.getItemRestId();
    } catch {
      // Item has no ID yet (e.g. compose email tab switch with stale origUrl context).
      // Returning without configuration is correct — compose components don't need it.
      this.loadingScreen.stopLoading();
      return;
    }

    if (emailId != this.sharedService.currentlySelectedEmailId) {
      this.bladeService.currentlySelectedContact = null;
      this.sharedService.currentlySelectedEmailId = emailId;

      try {
        return await Promise.all([
          this.configureAction(),
          this.configurePeople()
        ]);
      } catch {
        // Configuration failed (e.g. compose item with stale read-mode context).
        // Stop the spinner and resolve gracefully so navigation completes.
        this.loadingScreen.stopLoading();
      }
    }

    return Promise.resolve();
  }

  private async configureAction(): Promise<void> {
    const sender = this.sharedService.getEmailDetailsForCurrentEmail();
    const senderName = sender.displayName;
    const senderEMail = sender.emailAddress;

    const knownContactsForEmail = await this.so.findMailSender("", [senderEMail]);
    this.sharedService.knownContactsForEmail = knownContactsForEmail;

    const defaultContact = knownContactsForEmail.length === 1
      ? knownContactsForEmail[0]
      : null;
    this.sharedService.setupBlade(defaultContact ? defaultContact.FullName : senderName, senderEMail, knownContactsForEmail);
  }

  // On Outlook desktop, Office.js sets mailboxItem before itemId is populated.
  // Poll briefly so configure() always has a valid itemId to work with.
  private async waitForItemId(timeoutMs = 3000): Promise<void> {
    const start = Date.now();
    while (!this.hasResolvableItemId()) {
      if (Date.now() - start >= timeoutMs) return;
      await new Promise<void>(res => setTimeout(res, 50));
    }
  }

  // Fallback-aware: also true when the guard recovered a REST id via Graph for items
  // where Office.js withholds the itemId (S/MIME on Outlook for Mac).
  private hasResolvableItemId(): boolean {
    try {
      this.sharedService.getItemRestId();
      return true;
    } catch {
      return false;
    }
  }

  private async configurePeople(): Promise<void> {
    const recipients = this.sharedService.getAllEmailRecpieintsForCurrentEmail();
    const first10 = recipients.slice(0, 10);

    const first10Contacts = await this.so.findMailSender("", first10.map((i) => i.emailAddress));
    this.sharedService.currentRecipients = this.sharedService.includeUnknownRecipients(first10, first10Contacts);
  }
}
